import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { LfAlertPrefs } from "../components/LfAlertPrefs";
import { ProfileViewsChart } from "../components/ProfileViewsChart";
import { SeasonalEmailPref } from "../components/SeasonalEmailPref";
import { PriceDropPref } from "../components/dashboard/PriceDropPref";
import { LitterAlerts } from "../components/dashboard/LitterAlerts";
import { DemandInsights } from "../components/dashboard/DemandInsights";
import { SightingEmailPref } from "../components/SightingEmailPref";
import { ChangeEmailCard } from "../components/ChangeEmailCard";
import { Button } from "../components/ui/button";
import { LayoutGrid, MessageCircle, Heart, Plus, UserCog, ExternalLink, BellRing, Egg } from "lucide-react";
import { SavedSearches } from "../components/dashboard/SavedSearches";
import { BreederLitters } from "../components/dashboard/BreederLitters";
import { ProfileEditor } from "../components/dashboard/ProfileEditor";
import { TwoFactorCard, SecurityQuestionCard, BlockedUsersCard, NotificationPrefs } from "../components/dashboard/AccountCards";
import { VerificationCard } from "../components/dashboard/VerificationCard";
import { MyListings, Favorites } from "../components/dashboard/MyListings";
import { Messages } from "../components/dashboard/Messages";
import { CityPromptBanner } from "../components/dashboard/CityPromptBanner";

const TABS = [
  { id: "listings", key: "d_tab_listings", icon: LayoutGrid },
  { id: "messages", key: "d_tab_messages", icon: MessageCircle },
  { id: "favorites", key: "d_tab_favorites", icon: Heart },
  { id: "saved", key: "ss_tab", icon: BellRing },
  { id: "litters", key: "lt_tab", icon: Egg, breeder: true },
  { id: "profile", key: "d_tab_profile", icon: UserCog },
];

export default function Dashboard() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const tab = params.get("tab") || "listings";

  const setTab = (t) => { const n = new URLSearchParams(params); n.set("tab", t); n.delete("c"); setParams(n); };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold text-[#12234A]">{t("d_title")}</h1>
        <Button onClick={() => navigate("/post")} data-testid="dashboard-post-btn" className="rounded-full bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5"><Plus size={18} /> {t("d_post_ad")}</Button>
      </div>

      <CityPromptBanner key={user?.user_id} />

      <div className="flex gap-2 mt-6 border-b border-slate-200">
        {TABS.filter((tb) => !tb.breeder || user?.can_post_litters).map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`tab-${tb.id}`}
            className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm border-b-2 -mb-px transition-colors ${tab === tb.id ? "border-[#F5551E] text-[#F5551E]" : "border-transparent text-slate-500 hover:text-[#12234A]"}`}>
            <tb.icon size={17} /> {t(tb.key)}
          </button>
        ))}
        {(user?.verified_seller || user?.verified_business || user?.is_verified_seller) && (
          <a href={`/seller/${user.user_id}`} target="_blank" rel="noreferrer" data-testid="dashboard-public-profile-link"
            className="flex items-center gap-1.5 px-3 py-3 text-sm font-semibold text-[#F5551E] hover:underline">
            <ExternalLink size={15} /> {t("d_public_profile")}
          </a>
        )}
      </div>

      <div className="mt-6">
        {tab === "listings" && <MyListings navigate={navigate} />}
        {tab === "messages" && <Messages user={user} params={params} setParams={setParams} />}
        {tab === "favorites" && <Favorites />}
        {tab === "saved" && <><SavedSearches navigate={navigate} /><LitterAlerts /></>}
        {tab === "litters" && user?.can_post_litters && <><BreederLitters /><DemandInsights /></>}
        {tab === "profile" && (
          <div className="space-y-5">
            <ProfileEditor />
            <ChangeEmailCard user={user} />
            {user?.role === "admin" && <div className="max-w-xl"><ProfileViewsChart /></div>}
            {user?.role === "user" && <VerificationCard user={user} />}
            {user?.role === "user" && <TwoFactorCard user={user} />}
            {user?.role === "user" && <SecurityQuestionCard />}
            {user?.role === "user" && <BlockedUsersCard />}
            {user?.role === "user" && <NotificationPrefs user={user} />}
            {user?.role === "user" && <SeasonalEmailPref user={user} />}
            {user?.role === "user" && <PriceDropPref user={user} />}
            {user?.role === "user" && <LfAlertPrefs />}
            <SightingEmailPref user={user} />
          </div>
        )}
      </div>
    </div>
  );
}
